/**
 * Pure minimap world->screen mapping + mirroring math (LIVE INTEGRATION
 * BUGFIX §6). Split out of hud.ts's drawRadar so the mirroring contract has a
 * directly testable surface with no canvas/DOM involved:
 *
 *   "the local player always attacks screen-right, in either half, on either
 *    team" — world.teamAAttacksPositiveX flips at half time (rules/fsm.ts),
 *    so a literal world->screen mapping would visibly swap which side the
 *    local player's goal sits on the instant the half turns over.
 *
 * `computeMirrorSign` decides whether the WHOLE map should rotate 180°
 * (negate BOTH x and z together — never one axis alone, which would
 * mirror-flip handedness and put a winger's real flank on the wrong side of
 * the map). `projectX`/`projectZ` apply that same `sign` to every world
 * coordinate, so every shape drawn from them — goals, boxes, dots, the
 * facing arrow — rotates together and stays geometrically consistent.
 */

/** +1 if the local player's attack direction already agrees with world +X;
 *  -1 when it doesn't, meaning the whole map should rotate 180° so the local
 *  player still appears to attack screen-right. */
export function computeMirrorSign(localTeam: number, teamAAttacksPositiveX: boolean): 1 | -1 {
  const localAttacksPositiveX = (localTeam === 0) === teamAAttacksPositiveX;
  return localAttacksPositiveX ? 1 : -1;
}

/** World X -> radar canvas X, honouring the mirror `sign`. */
export function projectX(worldX: number, sign: 1 | -1, pitchLength: number, pad: number, iw: number): number {
  return pad + ((worldX * sign + pitchLength / 2) / pitchLength) * iw;
}

/** World Z -> radar canvas Y, honouring the mirror `sign`. */
export function projectZ(worldZ: number, sign: 1 | -1, pitchWidth: number, pad: number, ih: number): number {
  return pad + ((worldZ * sign + pitchWidth / 2) / pitchWidth) * ih;
}

/**
 * Screen-space tip of a facing/aim-yaw arrow rooted at world (px, pz).
 * The tip is computed in WORLD space first (px + cos(yaw)*lengthM, pz +
 * sin(yaw)*lengthM) and pushed through the SAME projectX/projectZ as the
 * base point, so it inherits the mirror rotation automatically — there is no
 * separate "yaw + PI when mirrored" branch to get backwards.
 */
export function projectArrowTip(
  px: number,
  pz: number,
  yaw: number,
  lengthM: number,
  sign: 1 | -1,
  pitchLength: number,
  pitchWidth: number,
  pad: number,
  iw: number,
  ih: number,
): { x: number; z: number } {
  return {
    x: projectX(px + Math.cos(yaw) * lengthM, sign, pitchLength, pad, iw),
    z: projectZ(pz + Math.sin(yaw) * lengthM, sign, pitchWidth, pad, ih),
  };
}
